<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import instagramClient from 'dashboard/api/channel/instagramClient';
import Button from 'dashboard/components-next/button/Button.vue';

const ICE_BREAKERS_LIMIT = 4;
const PERSISTENT_MENU_LIMIT = 5;

const channels = ref([]);
const isLoading = ref(false);
const busyInboxId = ref(null);
const iceBreakers = ref({});
const persistentMenu = ref({});
const expandedInboxId = ref(null);

// Os nomes crus da Meta não dizem nada para quem opera; o que importa é o que
// deixa de chegar no atendimento quando o campo não está assinado.
const FIELD_LABELS = {
  messages: 'Mensagens diretas',
  message_edit: 'Edição de mensagem pelo contato',
  message_reactions: 'Reações do contato',
  message_echoes: 'Eco de mensagens enviadas fora do Zoraxy',
  messaging_seen: 'Confirmação de leitura',
  messaging_postbacks: 'Toques em ice breakers e botões',
  messaging_referral: 'Origem de anúncio e link ig.me',
  messaging_optins: 'Opt-in',
  messaging_policy_enforcement: 'Avisos de restrição da conta',
  comments: 'Comentários em posts e reels',
  live_comments: 'Comentários de live',
  mentions: 'Menções à conta',
  standby: 'Modo espera (handover)',
  messaging_handover: 'Transferência entre apps (handover)',
  story_insights: 'Métricas de stories',
};

const fieldLabel = field => FIELD_LABELS[field] || field;

// `action: 'unblock'` chega quando a Meta libera a conta de volta; só o
// `block` deve aparecer como alerta.
const isBlocked = channel => channel.policy_enforcement?.action === 'block';

const formatDate = value => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const hasChannels = computed(() => channels.value.length > 0);

const loadChannels = async () => {
  isLoading.value = true;
  try {
    const { data } = await instagramClient.listChannels();
    channels.value = data.payload || [];
  } catch {
    channels.value = [];
  } finally {
    isLoading.value = false;
  }
};

// Uma única chamada traz os dois: a Meta devolve ice breakers e menu
// persistente no mesmo `messenger_profile`.
const loadMessengerProfile = async inboxId => {
  try {
    const { data } = await instagramClient.getMessengerProfile(inboxId);
    iceBreakers.value = {
      ...iceBreakers.value,
      [inboxId]: (data.payload?.ice_breakers || []).map(item => ({
        question: item.question || '',
        payload: item.payload || '',
      })),
    };
    persistentMenu.value = {
      ...persistentMenu.value,
      [inboxId]: (data.payload?.persistent_menu || []).map(item => ({
        title: item.title || '',
        type: item.type === 'web_url' ? 'web_url' : 'postback',
        payload: item.payload || '',
        url: item.url || '',
      })),
    };
  } catch {
    iceBreakers.value = { ...iceBreakers.value, [inboxId]: [] };
    persistentMenu.value = { ...persistentMenu.value, [inboxId]: [] };
  }
};

const toggleDetails = async inboxId => {
  if (expandedInboxId.value === inboxId) {
    expandedInboxId.value = null;
    return;
  }
  expandedInboxId.value = inboxId;
  if (!iceBreakers.value[inboxId]) await loadMessengerProfile(inboxId);
};

const replaceChannel = payload => {
  channels.value = channels.value.map(channel =>
    channel.inbox_id === payload.inbox_id ? payload : channel
  );
};

const resubscribe = async channel => {
  busyInboxId.value = channel.inbox_id;
  try {
    const { data } = await instagramClient.resubscribe(channel.inbox_id);
    replaceChannel(data.payload);
    useAlert('Webhooks reassinados no Instagram.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'Não foi possível reassinar os webhooks no Instagram.'
    );
  } finally {
    busyInboxId.value = null;
  }
};

const updateSetting = async (channel, key, value) => {
  busyInboxId.value = channel.inbox_id;
  try {
    const { data } = await instagramClient.updateChannel(channel.inbox_id, {
      [key]: value,
    });
    replaceChannel(data.payload);
  } catch {
    useAlert('Não foi possível salvar a configuração do canal.');
  } finally {
    busyInboxId.value = null;
  }
};

const addIceBreaker = inboxId => {
  const current = iceBreakers.value[inboxId] || [];
  if (current.length >= ICE_BREAKERS_LIMIT) return;
  iceBreakers.value = {
    ...iceBreakers.value,
    [inboxId]: [...current, { question: '', payload: '' }],
  };
};

const removeIceBreaker = (inboxId, index) => {
  const current = [...(iceBreakers.value[inboxId] || [])];
  current.splice(index, 1);
  iceBreakers.value = { ...iceBreakers.value, [inboxId]: current };
};

const addMenuItem = inboxId => {
  const current = persistentMenu.value[inboxId] || [];
  if (current.length >= PERSISTENT_MENU_LIMIT) return;
  persistentMenu.value = {
    ...persistentMenu.value,
    [inboxId]: [
      ...current,
      { title: '', type: 'postback', payload: '', url: '' },
    ],
  };
};

const removeMenuItem = (inboxId, index) => {
  const current = [...(persistentMenu.value[inboxId] || [])];
  current.splice(index, 1);
  persistentMenu.value = { ...persistentMenu.value, [inboxId]: current };
};

// Ice breakers e menu vão sempre juntos: a Meta substitui o perfil inteiro a
// cada POST, então enviar só um dos dois apagaria o outro.
const saveMessengerProfile = async inboxId => {
  busyInboxId.value = inboxId;
  const breakers = (iceBreakers.value[inboxId] || []).filter(item =>
    item.question.trim()
  );
  const menu = (persistentMenu.value[inboxId] || [])
    .filter(item => item.title.trim())
    .filter(item => item.type !== 'web_url' || item.url.trim());

  try {
    await instagramClient.updateMessengerProfile(inboxId, {
      ice_breakers: breakers,
      persistent_menu: menu,
    });
    await loadMessengerProfile(inboxId);
    useAlert('Ice breakers e menu persistente atualizados no Instagram.');
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        'O Instagram recusou a atualização do perfil de mensagens.'
    );
  } finally {
    busyInboxId.value = null;
  }
};

defineExpose({ reload: loadChannels });

onMounted(loadChannels);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <p class="max-w-2xl text-sm text-n-slate-11">
        Estado real da conexão na Meta: validade do token, quais eventos a conta
        está de fato assinada para receber e os atalhos que aparecem para quem
        abre a conversa.
      </p>
      <Button
        label="Atualizar"
        icon="i-lucide-refresh-cw"
        slate
        size="sm"
        :is-loading="isLoading"
        @click="loadChannels"
      />
    </div>

    <p v-if="!hasChannels && !isLoading" class="text-sm text-n-slate-10">
      Nenhuma conta Instagram conectada ainda. Conecte uma conta para liberar os
      controles de comentários, ice breakers e presença.
    </p>

    <article
      v-for="channel in channels"
      :key="channel.inbox_id"
      class="rounded-2xl border border-n-weak bg-n-solid-1 p-4"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <h4 class="text-base font-semibold text-n-slate-12">
            {{ channel.name }}
          </h4>
          <p class="text-xs text-n-slate-10">
            IGSID {{ channel.instagram_id }} · inbox #{{ channel.inbox_id }}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <span
            v-if="channel.reauthorization_required"
            class="rounded-full bg-n-ruby-3 px-3 py-1 text-xs font-semibold text-n-ruby-11"
          >
            Reautorização necessária
          </span>
          <span
            v-else-if="channel.expires_in_days !== null"
            class="rounded-full px-3 py-1 text-xs font-semibold"
            :class="
              channel.expires_in_days <= 10
                ? 'bg-n-amber-3 text-n-amber-11'
                : 'bg-n-green-3 text-n-green-11'
            "
          >
            Token expira em {{ channel.expires_in_days }} dias
          </span>
          <Button
            :label="expandedInboxId === channel.inbox_id ? 'Fechar' : 'Ajustar'"
            :icon="
              expandedInboxId === channel.inbox_id
                ? 'i-lucide-chevron-up'
                : 'i-lucide-sliders-horizontal'
            "
            slate
            size="sm"
            @click="toggleDetails(channel.inbox_id)"
          />
        </div>
      </div>

      <div class="mt-4 rounded-2xl border border-n-weak bg-n-alpha-2 p-4">
        <div class="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span
            class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
          >
            Eventos assinados na Meta
          </span>
          <Button
            label="Reassinar webhooks"
            icon="i-lucide-plug-zap"
            slate
            size="sm"
            :is-loading="busyInboxId === channel.inbox_id"
            @click="resubscribe(channel)"
          />
        </div>

        <div class="flex flex-wrap gap-2">
          <span
            v-for="field in channel.subscribed_fields"
            :key="`on-${field}`"
            class="rounded-full bg-n-green-3 px-3 py-1 text-xs text-n-green-11"
          >
            {{ fieldLabel(field) }}
          </span>
          <span
            v-for="field in channel.missing_fields"
            :key="`off-${field}`"
            class="rounded-full bg-n-amber-3 px-3 py-1 text-xs text-n-amber-11"
          >
            falta: {{ fieldLabel(field) }}
          </span>
        </div>

        <p
          v-if="channel.subscription_error"
          class="mt-3 rounded-lg bg-n-ruby-3 px-3 py-2 text-xs text-n-ruby-11"
        >
          A Meta recusou a última tentativa de assinatura:
          {{ channel.subscription_error }}. Enquanto isso, nenhum evento chega
          nesta caixa.
        </p>

        <p
          v-if="!channel.subscription_synced_at"
          class="mt-3 text-xs text-n-slate-10"
        >
          Esta conta foi conectada antes da ampliação dos eventos. Reassine para
          passar a receber comentários, menções, cliques em ice breakers e a
          origem dos anúncios.
        </p>
      </div>

      <div
        v-if="channel.deauthorized_at"
        class="mt-3 rounded-xl bg-n-ruby-3 px-4 py-3 text-xs text-n-ruby-11"
      >
        A conta removeu o acesso do app no Instagram em
        {{ formatDate(channel.deauthorized_at) }}. Nada entra nem sai até alguém
        reconectar a conta pela tela de caixas de entrada.
      </div>

      <div
        v-if="isBlocked(channel)"
        class="mt-3 rounded-xl bg-n-amber-3 px-4 py-3 text-xs text-n-amber-11"
      >
        A Meta restringiu o envio de mensagens desta conta em
        {{ formatDate(channel.policy_enforcement.received_at) }}.
        <template v-if="channel.policy_enforcement.reason">
          Motivo informado: {{ channel.policy_enforcement.reason }}.
        </template>
        Enquanto durar, todo envio será recusado.
      </div>

      <div v-if="expandedInboxId === channel.inbox_id" class="mt-4 grid gap-4">
        <div
          class="grid gap-3 rounded-2xl border border-n-weak p-4 md:grid-cols-3"
        >
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <input
              type="checkbox"
              :checked="channel.settings.mark_seen_enabled"
              @change="
                updateSetting(
                  channel,
                  'mark_seen_enabled',
                  $event.target.checked
                )
              "
            />
            Marcar como visto
          </label>
          <label class="flex items-center gap-2 text-sm text-n-slate-11">
            <input
              type="checkbox"
              :checked="channel.settings.typing_indicator_enabled"
              @change="
                updateSetting(
                  channel,
                  'typing_indicator_enabled',
                  $event.target.checked
                )
              "
            />
            Mostrar “digitando…”
          </label>
          <label class="flex flex-col gap-1 text-sm text-n-slate-11">
            Resposta a comentário
            <select
              class="rounded-lg border border-n-weak bg-n-background px-2 py-1 text-sm"
              :value="channel.settings.comment_reply_mode"
              @change="
                updateSetting(
                  channel,
                  'comment_reply_mode',
                  $event.target.value
                )
              "
            >
              <option value="private">Direct privado (converte em DM)</option>
              <option value="public">Resposta pública no comentário</option>
            </select>
          </label>
        </div>

        <div class="rounded-2xl border border-n-weak p-4">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h5 class="text-sm font-semibold text-n-slate-12">
                Ice breakers
              </h5>
              <p class="text-xs text-n-slate-10">
                Até {{ ICE_BREAKERS_LIMIT }} perguntas mostradas a quem abre a
                conversa pela primeira vez. O toque chega como mensagem no
                atendimento.
              </p>
            </div>
            <Button
              label="Adicionar"
              icon="i-lucide-plus"
              slate
              size="sm"
              :disabled="
                (iceBreakers[channel.inbox_id] || []).length >=
                ICE_BREAKERS_LIMIT
              "
              @click="addIceBreaker(channel.inbox_id)"
            />
          </div>

          <div class="grid gap-2">
            <div
              v-for="(item, index) in iceBreakers[channel.inbox_id] || []"
              :key="index"
              class="flex flex-wrap items-center gap-2"
            >
              <input
                v-model="item.question"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                placeholder="Pergunta exibida ao contato"
                maxlength="80"
              />
              <input
                v-model="item.payload"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                placeholder="Identificador interno (opcional)"
              />
              <Button
                icon="i-lucide-trash-2"
                slate
                size="sm"
                @click="removeIceBreaker(channel.inbox_id, index)"
              />
            </div>
          </div>
        </div>

        <div class="rounded-2xl border border-n-weak p-4">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h5 class="text-sm font-semibold text-n-slate-12">
                Menu persistente
              </h5>
              <p class="text-xs text-n-slate-10">
                Até {{ PERSISTENT_MENU_LIMIT }} atalhos fixos no rodapé da
                conversa, sempre visíveis. Use link para levar ao site e ação
                para gerar mensagem no atendimento.
              </p>
            </div>
            <Button
              label="Adicionar"
              icon="i-lucide-plus"
              slate
              size="sm"
              :disabled="
                (persistentMenu[channel.inbox_id] || []).length >=
                PERSISTENT_MENU_LIMIT
              "
              @click="addMenuItem(channel.inbox_id)"
            />
          </div>

          <div class="grid gap-2">
            <div
              v-for="(item, index) in persistentMenu[channel.inbox_id] || []"
              :key="`menu-${index}`"
              class="flex flex-wrap items-center gap-2"
            >
              <input
                v-model="item.title"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                placeholder="Texto do botão"
                maxlength="30"
              />
              <select
                v-model="item.type"
                class="rounded-lg border border-n-weak bg-n-background px-2 py-2 text-sm"
              >
                <option value="postback">Ação</option>
                <option value="web_url">Link</option>
              </select>
              <input
                v-if="item.type === 'web_url'"
                v-model="item.url"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                placeholder="https://…"
              />
              <input
                v-else
                v-model="item.payload"
                class="min-w-0 flex-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
                placeholder="Identificador interno (opcional)"
              />
              <Button
                icon="i-lucide-trash-2"
                slate
                size="sm"
                @click="removeMenuItem(channel.inbox_id, index)"
              />
            </div>
          </div>
        </div>

        <div class="flex justify-end">
          <Button
            label="Salvar ice breakers e menu"
            icon="i-lucide-save"
            size="sm"
            :is-loading="busyInboxId === channel.inbox_id"
            @click="saveMessengerProfile(channel.inbox_id)"
          />
        </div>
      </div>
    </article>
  </div>
</template>
